// No longer importing GoogleGenerativeAI
// import { GoogleGenerativeAI } from "@google/genai";

/**
 * Service for interacting with the backend API which proxies Gemini AI calls.
 */
class GeminiService {
  // Base URL for the backend API
  static #backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'; // Default if not set

  // Helper function to handle fetch responses
  static async handleResponse(response) {
    if (!response.ok) {
      const contentType = response.headers.get("content-type");
      let errorData = { error: `Backend error: ${response.status} ${response.statusText}` };
      let errorMessage = errorData.error;
      let isLoginRequired = false;

      if (contentType && contentType.includes("application/json")) {
        try {
          errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          // Check for the specific flag from our rate limiter
          isLoginRequired = errorData.loginRequired === true;
        } catch (parseError) {
          console.error('Failed to parse error JSON:', parseError);
        }
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      // Add login required flag to the error object
      if (isLoginRequired) {
        error.loginRequired = true;
      }
      throw error;
    }

    // Try to parse successful JSON response
    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      console.error('Error parsing successful JSON response:', parseError);
      throw new Error('Received invalid JSON response from server.');
    }
  }

  // Generic fetch helper
  static async #fetchAPI(endpoint, options) {
    const token = localStorage.getItem('authToken');
    const headers = { 
      ...(options.body instanceof FormData ? {} : {'Content-Type': 'application/json'}), // Omit Content-Type for FormData
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      const response = await fetch(`${this.#backendUrl}${endpoint}`, {
        ...options,
        headers,
      });
      return this.handleResponse(response); // Use the helper
    } catch (error) {
      console.error(`Error calling API endpoint ${endpoint}:`, error);
      // Pass loginRequired flag if it exists on the error
      return { 
        error: error.message || "Failed to communicate with backend", 
        status: error.status, 
        loginRequired: error.loginRequired,
        data: error.data,     // Include data if available
        html: null 
      };
    }
  }

  /**
   * Send a chat message to the backend and get a response
   * @param {string} message - The user's message
   * @param {string} deviceType - The target device type (web, mobile, tablet)
   * @returns {Promise<Object>} - Response containing type, content and potentially error
   */
  static async sendChatMessage(message, deviceType = 'web') {
    return this.#fetchAPI('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ message, deviceType }),
    });
  }

  /**
   * Generate UI by calling the backend endpoint (Plain Tailwind or Specialized).
   * @param {string} prompt - The user's text prompt for generating UI
   * @param {FormData|null} formData - Optional FormData for file uploads
   * @param {string} deviceType - The target device type (web, mobile, tablet)
   * @param {boolean} allowSpecializedUI - Whether to allow non-Tailwind CSS
   * @returns {Promise<Object>} - Response containing HTML and potentially rawResponse or error
   */
  static async generateUI(prompt, formData = null, deviceType = 'web', allowSpecializedUI = false) {
    const endpoint = formData ? '/api/generate-with-files' : '/api/generate';
    let body;
    if (formData) {
        // Append allowSpecializedUI to FormData if it doesn't exist
        if (!formData.has('allowSpecializedUI')) {
            formData.append('allowSpecializedUI', String(allowSpecializedUI)); // Convert boolean to string
        }
        // Ensure other required fields are present if not already added in App.js
        if (!formData.has('prompt')) formData.append('prompt', prompt);
        if (!formData.has('deviceType')) formData.append('deviceType', deviceType);
        body = formData;
    } else {
        // Send allowSpecializedUI instead of designStyle
        body = JSON.stringify({ prompt, deviceType, allowSpecializedUI });
    }
    return this.#fetchAPI(endpoint, { method: 'POST', body });
  }

  /**
   * Modify UI by calling the backend endpoint.
   * @param {string} elementDescription - Description of the element to modify
   * @param {string} userRequest - What the user wants to change
   * @param {string} currentHTML - The current HTML
   * @param {FormData|null} formData - Optional FormData for file uploads
   * @param {string} deviceType - The target device type (web, mobile, tablet)
   * @param {string} designStyle - The desired design style ('creative' or 'wireframe')
   * @returns {Promise<Object>} - Response containing updated HTML and potentially rawResponse or error
   */
  static async modifyUI(elementDescription, userRequest, currentHTML, formData = null, deviceType = 'web', designStyle = 'creative') {
    // Check if the element description indicates a hand-drawn element
    const isHandDrawn = typeof elementDescription === 'string' && 
                         elementDescription.includes('"type":"hand-drawn"');
    
    // If dealing with hand-drawn elements, we'll add more explicit instructions
    let enhancedUserRequest = userRequest;
    if (isHandDrawn) {
      const parsedDesc = JSON.parse(elementDescription);
      const drawings = parsedDesc.drawings || [];
      enhancedUserRequest = `Based on the user's drawing(s) described below, perform the following modification: "${userRequest}". Drawings context: ${JSON.stringify(drawings)}`;
    }
    
    const endpoint = formData ? '/api/modify-with-files' : '/api/modify';
    let body;
    if (formData) {
      // Append non-file fields if they weren't already (FormData might be pre-populated)
      if (!formData.has('elementDescription')) formData.append('elementDescription', elementDescription);
      if (!formData.has('userRequest')) formData.append('userRequest', enhancedUserRequest); // Use enhanced message
      if (!formData.has('currentHTML')) formData.append('currentHTML', currentHTML);
      if (!formData.has('deviceType')) formData.append('deviceType', deviceType);
      if (!formData.has('designStyle')) formData.append('designStyle', designStyle); // Ensure designStyle is appended
      body = formData;
    } else {
      body = JSON.stringify({ 
        elementDescription, 
        userRequest: enhancedUserRequest, // Use enhanced message
        currentHTML, 
        deviceType, 
        designStyle // Add designStyle
      });
    }
    return this.#fetchAPI(endpoint, { method: 'POST', body });
  }

  /**
   * Generate UI with shadcn/ui components by calling the backend endpoint.
   * This endpoint now always uses the 'creative' mode internally.
   * @param {string} prompt - The user's text prompt for generating UI
   * @param {FormData|null} formData - Optional FormData for file uploads
   * @param {string} deviceType - The target device type (web, mobile, tablet)
   * @returns {Promise<Object>} - Response containing HTML and potentially rawResponse or error
   */
  static async generateShadcnUI(prompt, formData = null, deviceType = 'web') {
    const endpoint = formData ? '/api/generate-shadcn-with-files' : '/api/generate-shadcn';
    let body;
    if (formData) {
       // Ensure required fields are present (prompt/deviceType might be added in App.js)
       if (!formData.has('prompt')) formData.append('prompt', prompt);
       if (!formData.has('deviceType')) formData.append('deviceType', deviceType);
       // No need to append style
       body = formData;
    } else {
       // Remove designStyle from body
       body = JSON.stringify({ prompt, deviceType }); 
    }
    return this.#fetchAPI(endpoint, { method: 'POST', body });
  }

  /**
   * Modify UI with shadcn/ui components by calling the backend endpoint.
   * This endpoint now always uses the 'creative' mode internally for shadcn.
   * @param {string} elementDescription - Description of the element to modify
   * @param {string} userRequest - What the user wants to change
   * @param {string} currentHTML - The current HTML
   * @param {FormData|null} formData - Optional FormData for file uploads
   * @param {string} deviceType - The target device type (web, mobile, tablet)
   * @returns {Promise<Object>} - Response containing updated HTML and potentially rawResponse or error
   */
  static async modifyShadcnUI(elementDescription, userRequest, currentHTML, formData = null, deviceType = 'web') {
    const endpoint = formData ? '/api/modify-shadcn-with-files' : '/api/modify-shadcn'; // Assuming -with-files endpoint exists
    let body;
    if (formData) {
       // Append non-file fields if they weren't already
       if (!formData.has('elementDescription')) formData.append('elementDescription', elementDescription);
       if (!formData.has('userRequest')) formData.append('userRequest', userRequest);
       if (!formData.has('currentHTML')) formData.append('currentHTML', currentHTML);
       if (!formData.has('deviceType')) formData.append('deviceType', deviceType);
       // No need to append style
       body = formData;
    } else {
       // Remove designStyle from body
       body = JSON.stringify({ elementDescription, userRequest, currentHTML, deviceType });
    }
    return this.#fetchAPI(endpoint, { method: 'POST', body });
  }

  /**
   * Optional: Check if the backend is healthy.
   * You might call this on startup or periodically.
   */
  static async checkBackendHealth() {
    try {
      const response = await fetch(`${this.#backendUrl}/api/health`);
      return response.ok;
    } catch (error) {
      console.error('Error checking backend health:', error);
      return false;
    }
  }
  
  // --- Deprecated/Removed Methods ---
  // static initialize(...) - No longer needed on frontend
  // static isConfigured(...) - Replaced by checkBackendHealth or implied success
  // static extractHTML(...) - Backend handles extraction now
}

export default GeminiService; 